import { AssignedBubble } from '@/components/global/AssignedBubble';
import { label } from '@/components/ui/embedded-resource-table/descriptor';
import { Badge } from '@/components/ui/shadcn/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { ProjectStatusOption } from '@/lib/constants-db';
import { settingLabel } from '@/lib/status-labels';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { StatusOption, type Project } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Building2 } from 'lucide-react';
import { InlineStatusCell } from './inline-cells/InlineStatusCell';
import { projectTableDescriptor } from './project-table-descriptor';

interface ProjectTableProps {
    projects: Project[];
    onUpdate?: {
        status?: (projectId: string, value: string) => Promise<void>;
    };
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide?: (column: string) => void;
    onColumnShow?: (column: string) => void;
    visibleColumns?: Set<string>;
    columnFilters?: Record<string, string[]>;
    columnFilterOptions?: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange?: (column: string, values: string[]) => void;
    statuses?: ProjectStatusOption[];
    readOnly?: boolean;
}

export function ProjectTable({
    projects,
    onUpdate,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
    visibleColumns = new Set(projectTableDescriptor.defaultVisibleColumns),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    statuses = [],
    readOnly = false,
}: ProjectTableProps) {
    const { t } = useTranslations();

    const handleRowClick = (projectId: string) => {
        router.visit(`/projects/${projectId}`);
    };

    const handleSort = (column: string, direction: 'asc' | 'desc') => {
        onSortChange?.(column, direction);
    };

    const handleColumnHide = (column: string) => {
        onColumnHide?.(column);
    };

    // Derived from the descriptor rather than restated here: this used to be a
    // third copy of the catalogue, alongside the index page's and the show tabs'.
    // A column with no client-side sort accessor and no server sort key offers no
    // ordering, rather than one that silently falls back to created_at.
    const columns = projectTableDescriptor.columns
        .filter((column) => visibleColumns.has(column.id))
        .map((column) => ({
            id: column.id,
            label: label(t, column),
            compact: column.compact,
            sortable: Boolean(column.sort),
        }));
    const truncate = (value: string, max = 30) => (value.length > max ? `${value.slice(0, max)}...` : value);
    const buildContractNumber = (project: Project) => {
        const baseNumber = project.contract_number ?? '';
        if (!project.manufacturing_number || baseNumber.includes('/')) {
            return baseNumber;
        }

        return `${baseNumber}/${project.manufacturing_number}`;
    };

    return (
        <div className="space-y-4">
            <div className="bg-card mb-4 items-center justify-between gap-3 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column) => {
                                const isSorted = sortColumn === column.id;
                                const currentSortDirection = isSorted ? sortDirection : null;
                                const selectedFilters = columnFilters[column.id] || [];

                                return (
                                    // The people columns hold one avatar, so they take no more
                                    // room than their own header label needs.
                                    <TableHead key={column.id} className={cn(column.compact && 'w-px')}>
                                        <TableColumnHeader
                                            label={column.label}
                                            sortDirection={currentSortDirection}
                                            onSortAscending={column.sortable ? () => handleSort(column.id, 'asc') : undefined}
                                            onSortDescending={column.sortable ? () => handleSort(column.id, 'desc') : undefined}
                                            onHide={() => handleColumnHide(column.id)}
                                            filterOptions={columnFilterOptions[column.id] || []}
                                            selectedFilters={selectedFilters}
                                            onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                            isFiltered={selectedFilters.length > 0}
                                        />
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {projects.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                                    {t('projects.index.empty.no_projects_found', 'No projects found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            projects.map((project) => {
                                return (
                                    <TableRow
                                        key={project.id}
                                        className={cn('cursor-pointer transition-all', 'hover:bg-muted/50')}
                                        onClick={() => handleRowClick(project.id)}
                                    >
                                        {columns.map((column) => {
                                            if (column.id === 'name') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Link
                                                            href={`/projects/${project.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-muted-foreground hover:bg-accent hover:text-foreground block w-full rounded px-2 py-1.5 text-left text-sm transition-colors"
                                                        >
                                                            <strong className="w-full">{buildContractNumber(project)}</strong>
                                                        </Link>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'contract_name') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Link
                                                            href={`/projects/${project.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            title={project.contract_name || undefined}
                                                            className="text-muted-foreground hover:bg-accent hover:text-foreground block w-full rounded px-2 py-1.5 text-left text-sm transition-colors"
                                                        >
                                                            {project.contract_name ? truncate(project.contract_name) : '—'}
                                                        </Link>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'company') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        {project.company ? (
                                                            <Link href={`/companies/${project.company.id}`}>
                                                                <div className="text-muted-foreground hover:bg-accent hover:text-foreground block flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors">
                                                                    <Building2 className="text-muted-foreground size-4" />
                                                                    <span>{project.company.name}</span>
                                                                </div>
                                                            </Link>
                                                        ) : (
                                                            <div className="text-muted-foreground">—</div>
                                                        )}
                                                    </TableCell>
                                                );
                                            }

                                            {
                                                /* No separate owner column: the Assigned column below shows the
                                                   owner alongside the manager and the engineer, and a name column
                                                   here would repeat a third of it. */
                                            }

                                            if (column.id === 'status') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineStatusCell
                                                            value={project.status}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.status
                                                                    ? async (value) => {
                                                                          await onUpdate.status!(project.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            statuses={statuses as unknown as StatusOption[]}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'country_type') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <div className="text-sm">
                                                            <Badge variant="outline" className="w-full text-left">
                                                                {/* The raw value is an English slug from config.json. */}
                                                                {project.country_type
                                                                    ? settingLabel(t, 'project_country_types', String(project.country_type))
                                                                    : '—'}
                                                            </Badge>
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'start_date') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <div className="text-sm">{project.start_date ? formatDate(project.start_date) : '—'}</div>
                                                    </TableCell>
                                                );
                                            }

                                            // if (column.id === 'end_date') {
                                            //     return (
                                            //         <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                            //             <div className="text-sm">
                                            //                 {project.end_date ? formatDate(project.end_date) : '—'}
                                            //             </div>
                                            //         </TableCell>
                                            //     );
                                            // }

                                            if (column.id === 'address_street') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <div className="text-sm">{project.address_street ? project.address_street : '—'}</div>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'sales_price') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <div className="text-sm font-medium">{formatCurrency(project.sales_price || 0)}</div>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'assigned') {
                                                return (
                                                    <TableCell key={column.id} className="w-px" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center gap-1.5">
                                                            <AssignedBubble person={project.owner} role={t('projects.tables.owner', 'Owner')} />
                                                            <AssignedBubble
                                                                person={project.project_manager}
                                                                role={t('projects.tables.project_manager', 'Project Manager')}
                                                            />
                                                            <AssignedBubble
                                                                person={project.project_engineer}
                                                                role={t('projects.tables.project_engineer', 'Engineer')}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            return null;
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
